import { createProCheckoutSession, getBillingStatus } from "../api";
import { openPaymentPage } from "../components/credits/openPaymentPage";
import { normalizeInternalReturnPath } from "./upgradeNavigation";

// Read by the pricing page when Stripe sends the customer back (success_url).
export const TRIAL_CHECKOUT_RETURN_KEY = "offlico-basic-upgrade-return";

const PRICING_PATHS = new Set(["/for-businesses/pricing", "/pricing"]);
const LOCAL_ORIGIN = "https://offlico.local";

// The free-trial buttons send people to sign-up with redirect=<pricing page
// that opens the Professional checkout> (marketing PROFESSIONAL_TRIAL_URL:
// /for-businesses/pricing?cycle=monthly&checkout=basic&returnTo=%2Fapp).
// Returns what that checkout needs, or null for any other address.
export function trialCheckoutFromRedirect(target) {
  if (typeof target !== "string" || !target.startsWith("/") || target.startsWith("//")) return null;
  let url;
  try {
    url = new URL(target, LOCAL_ORIGIN);
  } catch {
    return null;
  }
  if (url.origin !== LOCAL_ORIGIN) return null;
  const path = url.pathname.replace(/\/+$/, "") || "/";
  if (!PRICING_PATHS.has(path) || url.searchParams.get("checkout") !== "basic") return null;
  const interval = url.searchParams.get("cycle") === "annual" ? "annual" : "monthly";
  const returnTo = normalizeInternalReturnPath(url.searchParams.get("returnTo")) || "/app";
  // Back from Stripe lands on this pricing page, without opening checkout again.
  url.searchParams.delete("checkout");
  const query = url.searchParams.toString();
  return { interval, returnTo, pricingPath: `${url.pathname}${query ? `?${query}` : ""}` };
}

// The pricing page an account goes back to from Stripe when it was sent there
// from `returnTo` (an app page).
export function trialPricingPath(returnTo = "/app") {
  const params = new URLSearchParams({ cycle: "monthly" });
  const safeReturn = normalizeInternalReturnPath(returnTo);
  if (safeReturn) params.set("returnTo", safeReturn);
  return `/for-businesses/pricing?${params.toString()}`;
}

// Opens the Stripe form for the Professional free trial straight away (no
// pricing page first). Resolves true once the browser is on its way to Stripe;
// false when the account already has a paid plan or Stripe gave no address, so
// the caller shows the pricing page, which explains anything unusual. Throws
// when the checkout can't be started.
export async function openTrialCheckout({ interval = "monthly", returnTo = "/app", pricingPath } = {}) {
  const current = await getBillingStatus({ forceRefresh: true });
  if (current?.plan && current.plan !== "starter") return false;
  const checkout = await createProCheckoutSession({
    plan: "basic",
    interval,
    start_trial: interval === "monthly" && current?.trial_eligible !== false,
  });
  if (!checkout?.url) return false;
  try {
    window.sessionStorage.setItem(TRIAL_CHECKOUT_RETURN_KEY, normalizeInternalReturnPath(returnTo) || "/app");
  } catch {
    // Without it the pricing page returns to its own returnTo.
  }
  if (pricingPath) {
    try {
      // The address Back from Stripe opens: the pricing page, not this one.
      window.history.replaceState(window.history.state, "", pricingPath);
    } catch {
      // Keep the current address.
    }
  }
  openPaymentPage(checkout.url);
  return true;
}
